package dev.lingvo.site.api

import dev.lingvo.shared.ApplyDesignTemplateRequest
import dev.lingvo.shared.DesignAssetDto
import dev.lingvo.shared.DesignAssetKind
import dev.lingvo.shared.SaveDesignTemplateRequest
import dev.lingvo.shared.SavedDesignTemplateDto
import dev.lingvo.shared.ScreenshotDesignDto
import dev.lingvo.shared.StorePlatform
import dev.lingvo.shared.WorkspaceAssetDto
import dev.lingvo.shared.WorkspaceAssetImportRequest
import kotlinx.serialization.decodeFromString
import org.w3c.files.Blob

/** A project anchor retains project allowlists while the server resolves its workspace. */
suspend fun ApiClient.workspaceScreenshotAssets(slug: String, platform: StorePlatform): List<WorkspaceAssetDto> =
    jsonRequest("GET", libraryBase(slug, platform) + "/workspace-assets")

/** Uses the existing bounded upload transport and its cancellation/progress semantics. */
suspend fun ApiClient.uploadWorkspaceScreenshotAsset(
    slug: String, platform: StorePlatform, id: String, kind: DesignAssetKind, body: Blob,
    contentType: String, upload: XhrUpload, onProgress: (Long, Long) -> Unit,
): WorkspaceAssetDto = upload.put(libraryBase(slug, platform) + "/workspace-assets/${segment(id)}" + query("kind" to kind.name),
    body, contentType, onProgress = onProgress) { API_JSON.decodeFromString<WorkspaceAssetDto>(it) }

/** The returned identity may already exist when identical bytes were previously imported. */
suspend fun ApiClient.importWorkspaceScreenshotAsset(
    slug: String, platform: StorePlatform, id: String, localId: String,
): DesignAssetDto = jsonRequest("POST", libraryBase(slug, platform) + "/workspace-assets/${segment(id)}/import",
    WorkspaceAssetImportRequest(localId))

/** Deletion is refused by the server while current designs or retained pins use the artwork. */
suspend fun ApiClient.deleteWorkspaceScreenshotAsset(slug: String, platform: StorePlatform, id: String): Unit =
    unitRequest("DELETE", libraryBase(slug, platform) + "/workspace-assets/${segment(id)}")

/** The collection contains project-private entries and shared entries from this workspace. */
suspend fun ApiClient.savedScreenshotTemplates(slug: String, platform: StorePlatform): List<SavedDesignTemplateDto> =
    jsonRequest("GET", libraryBase(slug, platform) + "/templates")

/** Saves the exact acknowledged version, never an unsaved local draft. */
suspend fun ApiClient.saveScreenshotTemplate(
    slug: String, platform: StorePlatform, request: SaveDesignTemplateRequest,
): SavedDesignTemplateDto = jsonRequest("POST", libraryBase(slug, platform) + "/templates", request)

/** Appends a design version against the explicit head without touching captures. */
suspend fun ApiClient.applyScreenshotTemplate(
    slug: String, platform: StorePlatform, id: String, baseVersion: Int,
): ScreenshotDesignDto = jsonRequest("POST", libraryBase(slug, platform) + "/templates/${segment(id)}/apply",
    ApplyDesignTemplateRequest(baseVersion))

/** Immutable template identities prevent a stale request from deleting replacement content. */
suspend fun ApiClient.deleteScreenshotTemplate(slug: String, platform: StorePlatform, id: String): Unit =
    unitRequest("DELETE", libraryBase(slug, platform) + "/templates/${segment(id)}")

private fun libraryBase(slug: String, platform: StorePlatform): String =
    "/projects/${segment(slug)}/screenshots/${segment(platform.id)}"
